import { useState, useMemo, type ReactNode } from "react";
import {
  DndContext,
  DragOverlay,
  closestCorners,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragStartEvent,
  type DragEndEvent,
  type DragOverEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { useDroppable } from "@dnd-kit/core";
import { MoreHorizontal, Plus, ChevronDown, ChevronRight } from "lucide-react";
import { cn } from "../../lib/utils";
import { Button } from "./button";

// --- Types ---

interface KanbanBoardProps<T extends { id: string }> {
  items: T[];
  groupByField: string;
  getGroupValue: (item: T) => string;
  columns: string[];
  renderCard: (item: T) => ReactNode;
  onDragEnd: (itemId: string, fromColumn: string, toColumn: string) => void;
  columnColors?: Record<string, string>;
  showEmptyColumns?: boolean;
}

// --- SortableCard ---

function SortableCard<T extends { id: string }>({
  item,
  renderCard,
}: {
  item: T;
  renderCard: (item: T) => ReactNode;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      className={cn(
        "cursor-grab active:cursor-grabbing",
        isDragging && "opacity-40"
      )}
    >
      {renderCard(item)}
    </div>
  );
}

// --- DroppableColumn ---

function DroppableColumn<T extends { id: string }>({
  columnId,
  items,
  renderCard,
  color,
  onDragEnd,
}: {
  columnId: string;
  items: T[];
  renderCard: (item: T) => ReactNode;
  color?: string;
  onDragEnd: KanbanBoardProps<T>["onDragEnd"];
}) {
  const [collapsed, setCollapsed] = useState(false);
  const { setNodeRef, isOver } = useDroppable({ id: columnId });

  return (
    <div className="flex flex-col min-w-[250px] flex-1">
      {/* Column Header */}
      <div className="flex items-center gap-2 px-1 pb-3">
        <button
          onClick={() => setCollapsed(!collapsed)}
          className="text-muted-foreground hover:text-foreground transition-colors"
        >
          {collapsed ? (
            <ChevronRight className="h-3.5 w-3.5" />
          ) : (
            <ChevronDown className="h-3.5 w-3.5" />
          )}
        </button>
        {color && (
          <span
            className="w-2.5 h-2.5 rounded-full flex-shrink-0"
            style={{ backgroundColor: color }}
          />
        )}
        <span className="text-sm font-medium text-foreground truncate">
          {columnId}
        </span>
        <span className="text-xs text-muted-foreground font-medium ml-0.5">
          {items.length}
        </span>
        <div className="flex-1" />
        <Button
          variant="ghost"
          size="icon"
          className="h-6 w-6 text-muted-foreground opacity-0 group-hover/col:opacity-100 transition-opacity"
        >
          <MoreHorizontal className="h-3.5 w-3.5" />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          className="h-6 w-6 text-muted-foreground opacity-0 group-hover/col:opacity-100 transition-opacity"
        >
          <Plus className="h-3.5 w-3.5" />
        </Button>
      </div>

      {/* Column Content */}
      {!collapsed && (
        <div
          ref={setNodeRef}
          className={cn(
            "flex-1 flex flex-col gap-2 min-h-[100px] rounded-lg p-1 transition-colors",
            isOver && "bg-accent-tint/50"
          )}
        >
          <SortableContext
            items={items.map((i) => i.id)}
            strategy={verticalListSortingStrategy}
          >
            {items.map((item) => (
              <SortableCard
                key={item.id}
                item={item}
                renderCard={renderCard}
              />
            ))}
          </SortableContext>
        </div>
      )}
    </div>
  );
}

// --- KanbanBoard ---

export function KanbanBoard<T extends { id: string }>({
  items,
  groupByField,
  getGroupValue,
  columns,
  renderCard,
  onDragEnd,
  columnColors,
  showEmptyColumns = false,
}: KanbanBoardProps<T>) {
  const [activeId, setActiveId] = useState<string | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor)
  );

  const groupedItems = useMemo(() => {
    const groups: Record<string, T[]> = {};
    for (const col of columns) {
      groups[col] = [];
    }
    for (const item of items) {
      const val = getGroupValue(item);
      if (!groups[val]) {
        groups[val] = [];
      }
      groups[val].push(item);
    }
    return groups;
  }, [items, columns, getGroupValue]);

  const activeItem = activeId
    ? items.find((i) => i.id === activeId)
    : null;

  const displayColumns = showEmptyColumns
    ? columns
    : columns.filter((col) => (groupedItems[col]?.length ?? 0) > 0);

  // Find which column an item belongs to
  function findColumn(itemId: string): string | undefined {
    for (const [col, colItems] of Object.entries(groupedItems)) {
      if (colItems.some((i) => i.id === itemId)) return col;
    }
    return undefined;
  }

  function handleDragStart(event: DragStartEvent) {
    setActiveId(event.active.id as string);
  }

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    setActiveId(null);

    if (!over) return;

    const activeItemId = active.id as string;
    const fromColumn = findColumn(activeItemId);

    // Determine target column: either directly over a column, or over an item in a column
    let toColumn = over.id as string;
    if (!columns.includes(toColumn)) {
      toColumn = findColumn(toColumn) || toColumn;
    }

    if (fromColumn && toColumn && fromColumn !== toColumn) {
      onDragEnd(activeItemId, fromColumn, toColumn);
    }
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCorners}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      <div className="flex gap-5 overflow-x-auto pb-4 px-1">
        {displayColumns.map((col) => (
          <div key={col} className="group/col">
            <DroppableColumn
              columnId={col}
              items={groupedItems[col] || []}
              renderCard={renderCard}
              color={columnColors?.[col]}
              onDragEnd={onDragEnd}
            />
          </div>
        ))}
      </div>

      <DragOverlay>
        {activeItem ? (
          <div className="rotate-[2deg] opacity-90 shadow-lg">
            {renderCard(activeItem)}
          </div>
        ) : null}
      </DragOverlay>
    </DndContext>
  );
}
